// Ticket search: the query language, and the bridge to the server RPC.
//
//   refund "password reset" -cancelled from:liz@acme.com status:closed
//   assignee:me priority:p1,p2 before:2026-09-01 has:attachment is:archived
//   invoice OR receipt
//
// Everything here is pure (no React, no Supabase, no DOM) so the root vitest
// suite can import it. The SERVER never sees this syntax: parseSearchQuery
// turns the text into structured values and search_tickets_v2 (migration
// 076) turns each term into a safe tsquery. No user text is ever spliced
// into SQL or a PostgREST filter string.

import { GROUP_KEYS, type GroupKey } from './ticket-groups';

// ── Limits ──────────────────────────────────────────────────────────────────

export const MAX_QUERY_CHARS = 300;
export const MAX_TERMS = 12;
const MIN_TERM_CHARS = 2;

// ── Types ───────────────────────────────────────────────────────────────────

// A type alias (not an interface) so arrays of it are assignable to the
// generated `Json` argument type of the RPC.
export type SearchTerm = {
  /** The word or phrase, as typed (the server lower-cases and cleans it). */
  t: string;
  /** True for a "quoted phrase" (matched as an exact word sequence). */
  p: boolean;
};

export interface ParsedSearch {
  /** AND of OR-groups: `a OR b c` => [[a, b], [c]]. */
  clauses: SearchTerm[][];
  /** `-word` / `-"phrase"`. */
  exclude: SearchTerm[];
  from: string[];
  to: string[];
  assignee: { me: boolean; none: boolean; names: string[] };
  statuses: string[];
  priorities: string[];
  categories: string[];
  /** YYYY-MM-DD, on or after this day (Sydney). */
  after: string | null;
  /** YYYY-MM-DD, strictly before this day (Sydney). */
  before: string | null;
  hasAttachment: boolean;
  /** `is:archived` etc. Overrides the active tab. */
  is: GroupKey | null;
  /** Human-readable problems (bad date, unknown status…). The rest of the
   *  query still runs. */
  errors: string[];
}

export type SearchMatchSource =
  | 'id'
  | 'subject'
  | 'sender'
  | 'contact'
  | 'note'
  | 'message'
  | 'recipient'
  | 'summary';

export interface SearchMatch {
  source: SearchMatchSource;
  /** Server snippet with \u0002 / \u0003 around hits (see snippetSegments). */
  snippet?: string;
  /** Plain text, no markers: highlight client-side with highlightSegments. */
  text?: string;
  message_id?: string;
  direction?: string;
  from_email?: string;
  message_at?: string;
  note_id?: string;
  author_slack_id?: string;
  message_hits?: number;
}

export interface TicketSearchRow<T = { id: string }> {
  ticket: T;
  has_attachment: boolean;
  rank: number;
  match: SearchMatch | null;
}

export interface TicketSearchPage<T = { id: string }> {
  total: number;
  counts: Record<GroupKey, number>;
  assignee_counts: Record<string, number>;
  /** How many tickets in this tab (and chips) are unread for me. */
  unread: number;
  rows: Array<TicketSearchRow<T>>;
}

// ── Tokenizer ───────────────────────────────────────────────────────────────

const OPERATOR_KEYS = new Set([
  'from',
  'to',
  'assignee',
  'status',
  'priority',
  'category',
  'before',
  'after',
  'has',
  'is',
]);

interface Token {
  /** Exactly what the user typed for this token (used by stripOperator). */
  raw: string;
  neg: boolean;
  /** Lower-cased operator key, or null for a plain word / phrase. */
  key: string | null;
  value: string;
  phrase: boolean;
}

function unquote(s: string): { value: string; quoted: boolean } {
  if (s.startsWith('"')) {
    const inner = s.slice(1);
    return { value: inner.endsWith('"') ? inner.slice(0, -1) : inner, quoted: true };
  }
  return { value: s, quoted: false };
}

function parseToken(raw: string): Token {
  let body = raw;
  let neg = false;
  if (body.length > 1 && body[0] === '-') {
    neg = true;
    body = body.slice(1);
  }
  const m = /^([A-Za-z]+):(.*)$/.exec(body);
  if (m && OPERATOR_KEYS.has(m[1].toLowerCase())) {
    const { value } = unquote(m[2]);
    return { raw, neg, key: m[1].toLowerCase(), value: value.trim(), phrase: false };
  }
  const { value, quoted } = unquote(body);
  return { raw, neg, key: null, value: value.trim(), phrase: quoted };
}

function tokenize(input: string): Token[] {
  const s = input.slice(0, MAX_QUERY_CHARS);
  const tokens: Token[] = [];
  let i = 0;
  while (i < s.length) {
    while (i < s.length && /\s/.test(s[i])) i++;
    if (i >= s.length) break;
    const start = i;
    let inQuote = false;
    while (i < s.length && (inQuote || !/\s/.test(s[i]))) {
      if (s[i] === '"') inQuote = !inQuote;
      i++;
    }
    tokens.push(parseToken(s.slice(start, i)));
  }
  return tokens;
}

// ── Operator value tables ───────────────────────────────────────────────────

const STATUS_ALIASES: Record<string, string[]> = {
  open: ['new', 'in_progress', 'waiting_on_client'],
  new: ['new'],
  progress: ['in_progress'],
  inprogress: ['in_progress'],
  in_progress: ['in_progress'],
  waiting: ['waiting_on_client'],
  waiting_on_client: ['waiting_on_client'],
  resolved: ['resolved'],
  closed: ['closed'],
};

const CATEGORIES = new Set([
  'billing',
  'technical',
  'complaint',
  'sales',
  'faq',
  'onboarding',
  'other',
]);

function isRealDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

function splitList(value: string): string[] {
  return value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

function addUnique(list: string[], v: string) {
  if (!list.includes(v)) list.push(v);
}

// ── Parser ──────────────────────────────────────────────────────────────────

export function parseSearchQuery(raw: string): ParsedSearch {
  const out: ParsedSearch = {
    clauses: [],
    exclude: [],
    from: [],
    to: [],
    assignee: { me: false, none: false, names: [] },
    statuses: [],
    priorities: [],
    categories: [],
    after: null,
    before: null,
    hasAttachment: false,
    is: null,
    errors: [],
  };
  if (raw.length > MAX_QUERY_CHARS) {
    out.errors.push(`Search is limited to ${MAX_QUERY_CHARS} characters; the rest was ignored.`);
  }

  let termCount = 0;
  let orNext = false;
  let tooMany = false;

  const addTerm = (term: SearchTerm, neg: boolean) => {
    if (termCount >= MAX_TERMS) {
      tooMany = true;
      return;
    }
    termCount += 1;
    if (neg) {
      out.exclude.push(term);
      return;
    }
    if (orNext && out.clauses.length > 0) {
      out.clauses[out.clauses.length - 1].push(term);
    } else {
      out.clauses.push([term]);
    }
  };

  for (const tok of tokenize(raw)) {
    // `OR` between two terms (uppercase, bare).
    if (!tok.key && !tok.neg && !tok.phrase && tok.raw === 'OR') {
      orNext = out.clauses.length > 0;
      continue;
    }

    if (tok.key) {
      orNext = false;
      if (tok.neg) {
        out.errors.push(`"-${tok.key}:" is not supported. Put "-" in front of a word or phrase.`);
        continue;
      }
      applyOperator(out, tok.key, tok.value);
      continue;
    }

    const value = tok.value;
    if (value.length < MIN_TERM_CHARS) {
      orNext = false;
      continue;
    }
    addTerm({ t: value, p: tok.phrase }, tok.neg);
    orNext = false;
  }

  if (tooMany) {
    out.errors.push(`Only the first ${MAX_TERMS} words are searched.`);
  }
  return out;
}

function applyOperator(out: ParsedSearch, key: string, value: string) {
  if (value === '') {
    out.errors.push(`"${key}:" needs a value.`);
    return;
  }
  const v = value.toLowerCase();
  switch (key) {
    case 'from':
      addUnique(out.from, v);
      return;
    case 'to':
      addUnique(out.to, v);
      return;
    case 'assignee':
      if (v === 'me') out.assignee.me = true;
      else if (v === 'none' || v === 'unassigned') out.assignee.none = true;
      else addUnique(out.assignee.names, v);
      return;
    case 'status':
      for (const item of splitList(v)) {
        const mapped = STATUS_ALIASES[item];
        if (!mapped) {
          out.errors.push(`Unknown status "${item}". Try open, new, progress, waiting, resolved or closed.`);
          continue;
        }
        for (const s of mapped) addUnique(out.statuses, s);
      }
      return;
    case 'priority':
      for (const item of splitList(v)) {
        const m = /^p?([1-4])$/.exec(item);
        if (!m) {
          out.errors.push(`Unknown priority "${item}". Use p1, p2, p3 or p4.`);
          continue;
        }
        addUnique(out.priorities, `P${m[1]}`);
      }
      return;
    case 'category':
      for (const item of splitList(v)) {
        if (!CATEGORIES.has(item)) {
          out.errors.push(`Unknown category "${item}".`);
          continue;
        }
        addUnique(out.categories, item);
      }
      return;
    case 'before':
    case 'after':
      if (!isRealDate(v)) {
        out.errors.push(`"${key}:" needs a date like 2026-09-01 (got "${value}").`);
        return;
      }
      if (key === 'before') out.before = v;
      else out.after = v;
      return;
    case 'has':
      if (v === 'attachment' || v === 'attachments' || v === 'file') out.hasAttachment = true;
      else out.errors.push(`"has:" only supports "attachment" (got "${value}").`);
      return;
    case 'is':
      if ((GROUP_KEYS as readonly string[]).includes(v)) out.is = v as GroupKey;
      else
        out.errors.push(
          `Unknown "is:${value}". Use open, mine, team, unassigned, watching, snoozed, archived or all.`,
        );
      return;
  }
}

/** True when the parsed query will actually search or filter something. */
export function hasSearchContent(p: ParsedSearch): boolean {
  return (
    p.clauses.length > 0 ||
    p.exclude.length > 0 ||
    p.from.length > 0 ||
    p.to.length > 0 ||
    p.assignee.me ||
    p.assignee.none ||
    p.assignee.names.length > 0 ||
    p.statuses.length > 0 ||
    p.priorities.length > 0 ||
    p.categories.length > 0 ||
    p.after !== null ||
    p.before !== null ||
    p.hasAttachment ||
    p.is !== null
  );
}

/** Plain words and phrases to highlight in subjects and snippets. */
export function highlightTermsOf(p: ParsedSearch): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const clause of p.clauses) {
    for (const t of clause) {
      const k = t.t.toLowerCase();
      if (!seen.has(k)) {
        seen.add(k);
        out.push(t.t);
      }
    }
  }
  return out;
}

/** Remove every `key:` operator token from the raw text (used when the user
 *  clicks a tab while `is:archived` is in the box). */
export function stripOperator(raw: string, key: string): string {
  return tokenize(raw)
    .filter((t) => t.key !== key.toLowerCase())
    .map((t) => t.raw)
    .join(' ');
}

// ── Bridge to the RPC ───────────────────────────────────────────────────────

export interface SearchMember {
  slack_user_id: string;
  name?: string | null;
  email?: string | null;
}

export interface SearchContext {
  inboxId: string;
  /** The dashboard tab, or 'any' (every tab: the Ctrl+K palette). */
  group: GroupKey | 'any';
  /** The dashboard chips. */
  filters: {
    priorities?: string[];
    statuses?: string[];
    categories?: string[];
    assigneeSlackIds?: string[];
    isExistingClient?: boolean;
    createdFrom?: string;
    createdTo?: string;
    dateField?: string;
    /** The Unread chip (migration 081). */
    unread?: boolean;
  };
  sort: string;
  mySlackId: string | null;
  members: SearchMember[];
  limit?: number;
  offset?: number;
}

export interface SearchRpcArgs {
  p_inbox_id: string;
  p_terms: SearchTerm[][];
  p_exclude: SearchTerm[];
  p_from: string[] | null;
  p_to: string[] | null;
  p_assignees: string[] | null;
  p_unassigned: boolean;
  p_statuses: string[] | null;
  p_priorities: string[] | null;
  p_categories: string[] | null;
  p_existing_client: boolean | null;
  p_has_attachment: boolean | null;
  p_date_field: string;
  p_date_from: string | null;
  p_date_to: string | null;
  p_after: string | null;
  p_before: string | null;
  p_group: GroupKey | 'any';
  p_sort: string;
  p_limit: number;
  p_offset: number;
  /** true = only tickets that are unread for me (migration 081); null = no filter. */
  p_unread: boolean | null;
}

/** Chips and operators both narrow the result: an empty intersection means
 *  "match nothing", which the server expresses as an empty array. */
function intersect(chips: string[] | undefined, op: string[]): string[] | null {
  const hasChips = !!chips && chips.length > 0;
  if (!hasChips && op.length === 0) return null;
  if (!hasChips) return op;
  if (op.length === 0) return chips!;
  return chips!.filter((c) => op.includes(c));
}

function memberMatches(m: SearchMember, term: string): boolean {
  const words = (m.name ?? '').toLowerCase().split(/\s+/).filter(Boolean);
  if (words.some((w) => w.startsWith(term))) return true;
  const local = (m.email ?? '').toLowerCase().split('@')[0];
  return local.startsWith(term);
}

export function buildSearchRpcArgs(
  parsed: ParsedSearch,
  ctx: SearchContext,
): { args: SearchRpcArgs; errors: string[] } {
  const errors = [...parsed.errors];

  // Assignee: chips AND operator, `none` meaning "unassigned".
  const opIds: string[] = [];
  if (parsed.assignee.me && ctx.mySlackId) opIds.push(ctx.mySlackId);
  for (const name of parsed.assignee.names) {
    const hits = ctx.members.filter((m) => memberMatches(m, name));
    if (hits.length === 0) errors.push(`No team member matches "${name}".`);
    for (const m of hits) if (!opIds.includes(m.slack_user_id)) opIds.push(m.slack_user_id);
  }
  const opWantsPeople =
    (parsed.assignee.me && !!ctx.mySlackId) || parsed.assignee.names.length > 0;
  const chips = ctx.filters.assigneeSlackIds;
  const hasChips = !!chips && chips.length > 0;

  let assignees: string[] | null = null;
  let unassigned = false;
  if (hasChips) {
    // Chips demand an assigned ticket, so `assignee:none` cannot also match.
    assignees = opWantsPeople ? chips!.filter((c) => opIds.includes(c)) : chips!;
    if (parsed.assignee.none && !opWantsPeople) assignees = [];
  } else if (opWantsPeople) {
    assignees = opIds;
    unassigned = parsed.assignee.none;
  } else if (parsed.assignee.none) {
    unassigned = true;
  }
  if (parsed.assignee.me && !ctx.mySlackId) {
    errors.push('"assignee:me" needs you to be signed in as a team member.');
  }

  const args: SearchRpcArgs = {
    p_inbox_id: ctx.inboxId,
    p_terms: parsed.clauses,
    p_exclude: parsed.exclude,
    p_from: parsed.from.length > 0 ? parsed.from : null,
    p_to: parsed.to.length > 0 ? parsed.to : null,
    p_assignees: assignees,
    p_unassigned: unassigned,
    p_statuses: intersect(ctx.filters.statuses, parsed.statuses),
    p_priorities: intersect(ctx.filters.priorities, parsed.priorities),
    p_categories: intersect(ctx.filters.categories, parsed.categories),
    p_existing_client:
      typeof ctx.filters.isExistingClient === 'boolean' ? ctx.filters.isExistingClient : null,
    p_has_attachment: parsed.hasAttachment ? true : null,
    p_date_field: ctx.filters.dateField ?? 'created_at',
    p_date_from: ctx.filters.createdFrom ?? null,
    p_date_to: ctx.filters.createdTo ?? null,
    p_after: parsed.after,
    p_before: parsed.before,
    p_group: parsed.is ?? ctx.group,
    p_sort: ctx.sort,
    p_limit: ctx.limit ?? 50,
    p_offset: ctx.offset ?? 0,
    p_unread: ctx.filters.unread ? true : null,
  };
  return { args, errors };
}

// ── Rendering helpers ───────────────────────────────────────────────────────

export interface Segment {
  text: string;
  hit: boolean;
}

const HIT_START = '\u0002';
const HIT_END = '\u0003';

/** Splits a server snippet on its \u0002/\u0003 markers. Tolerates unbalanced
 *  markers; the text itself is never interpreted as markup. */
export function snippetSegments(s: string): Segment[] {
  const out: Segment[] = [];
  let hit = false;
  let buf = '';
  const flush = () => {
    if (buf) out.push({ text: buf, hit });
    buf = '';
  };
  for (const ch of s) {
    if (ch === HIT_START) {
      flush();
      hit = true;
    } else if (ch === HIT_END) {
      flush();
      hit = false;
    } else {
      buf += ch;
    }
  }
  flush();
  return out;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Case-insensitive highlight of `terms` inside `text`; overlapping or
 *  adjacent hits merge. */
export function highlightSegments(text: string, terms: string[]): Segment[] {
  const clean = Array.from(
    new Set(terms.map((t) => t.trim()).filter((t) => t.length >= MIN_TERM_CHARS)),
  ).sort((a, b) => b.length - a.length);
  if (!text || clean.length === 0) return text ? [{ text, hit: false }] : [];

  const re = new RegExp(clean.map(escapeRegExp).join('|'), 'gi');
  const ranges: Array<[number, number]> = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m[0].length === 0) {
      re.lastIndex += 1;
      continue;
    }
    const last = ranges[ranges.length - 1];
    if (last && m.index <= last[1]) last[1] = Math.max(last[1], m.index + m[0].length);
    else ranges.push([m.index, m.index + m[0].length]);
  }

  const out: Segment[] = [];
  let pos = 0;
  for (const [a, b] of ranges) {
    if (a > pos) out.push({ text: text.slice(pos, a), hit: false });
    out.push({ text: text.slice(a, b), hit: true });
    pos = b;
  }
  if (pos < text.length) out.push({ text: text.slice(pos), hit: false });
  return out;
}

// ── Response parsing ────────────────────────────────────────────────────────

const SOURCES = new Set<string>([
  'id',
  'subject',
  'sender',
  'contact',
  'note',
  'message',
  'recipient',
  'summary',
]);

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function parseMatch(v: unknown): SearchMatch | null {
  const r = asRecord(v);
  if (!r) return null;
  const source = str(r.source);
  if (!source || !SOURCES.has(source)) return null;
  return {
    source: source as SearchMatchSource,
    snippet: str(r.snippet),
    text: str(r.text),
    message_id: str(r.message_id),
    direction: str(r.direction),
    from_email: str(r.from_email),
    message_at: str(r.message_at),
    note_id: str(r.note_id),
    author_slack_id: str(r.author_slack_id),
    message_hits: typeof r.message_hits === 'number' ? r.message_hits : undefined,
  };
}

/** The RPC returns untyped jsonb; this makes it safe to render. */
export function parseSearchResponse<T extends { id: string }>(json: unknown): TicketSearchPage<T> {
  const r = asRecord(json) ?? {};

  const counts = {} as Record<GroupKey, number>;
  const rawCounts = asRecord(r.counts) ?? {};
  for (const k of GROUP_KEYS) counts[k] = num(rawCounts[k]);

  const assignee_counts: Record<string, number> = {};
  const rawAssignees = asRecord(r.assignee_counts) ?? {};
  for (const [k, v] of Object.entries(rawAssignees)) assignee_counts[k] = num(v);

  const rows: Array<TicketSearchRow<T>> = [];
  if (Array.isArray(r.rows)) {
    for (const item of r.rows) {
      const row = asRecord(item);
      const ticket = asRecord(row?.ticket);
      if (!row || !ticket || typeof ticket.id !== 'string') continue;
      rows.push({
        ticket: ticket as unknown as T,
        has_attachment: row.has_attachment === true,
        rank: num(row.rank),
        match: parseMatch(row.match),
      });
    }
  }

  return { total: num(r.total, rows.length), counts, assignee_counts, unread: num(r.unread), rows };
}
